import React, { useState, useEffect, useRef } from 'react';
import { Camera, Smile, Zap, Music, User, Maximize2, Minimize2 } from 'lucide-react';
import { usePerformanceAnalysis } from '../hooks/usePerformanceAnalysis';
import { useAuth } from '../hooks/useAuth';
import Navbar from './Navbar';
import PaymentModal from './PaymentModal';
import AdPlaceholder from './AdPlaceholder';

const MetricBar = ({ label, value, icon: Icon, color }) => (
    <div className="mb-4">
        <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-white flex items-center gap-2">
                <Icon size={16} /> {label}
            </span>
            <span className="text-sm font-medium text-white">{value}%</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2.5">
            <div
                className="h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${value}%`, backgroundColor: color }}
            ></div>
        </div>
    </div>
);

const PerformanceArtTest = () => {
    const {
        isLoading, error, cameraReady, videoRef, pitchHz, pitchScore,
        initializeCamera, stopCamera, computeMetrics
    } = usePerformanceAnalysis();

    const { user } = useAuth(); // Auth Context

    const [isLive, setIsLive] = useState(false);
    const isLiveRef = useRef(false); // Ref to track live state inside loop

    // Sync ref with state
    useEffect(() => {
        isLiveRef.current = isLive;
    }, [isLive]);

    const [timeLeft, setTimeLeft] = useState(60);
    const [metrics, setMetrics] = useState({ expression: 0, movement: 0, pitch: 0, posture: 50 });
    const [history, setHistory] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

    // Recording Refs
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const videoContainerRef = useRef(null);

    const loopRef = useRef();
    const canvasRef = useRef(null);
    const prevMetricsRef = useRef({ expression: 0, movement: 0, pitch: 0, posture: 50 });
    const historyRef = useRef([]); // Track metrics history for final average

    // Lifecycle
    useEffect(() => {
        return () => {
            stopCamera();
            if (loopRef.current) cancelAnimationFrame(loopRef.current);
        };
    }, [stopCamera]);

    // Main Processing Loop with Bounding Box
    const startLoop = () => {
        const run = () => {
            const result = computeMetrics(performance.now());
            if (result) {
                const { metrics: currentMetrics, landmarks } = result;
                setMetrics(currentMetrics);

                // Collect metrics history during live session
                // Use ref to avoid closure staleness
                if (isLiveRef.current) {
                    historyRef.current.push(currentMetrics);
                }

                // Draw bounding box with landmarks
                drawBoundingBox(currentMetrics, landmarks);
            }
            loopRef.current = requestAnimationFrame(run);
        };
        run();
    };

    const drawBoundingBox = (currentMetrics, landmarks) => {
        if (!canvasRef.current || !videoRef.current) return;

        const canvas = canvasRef.current;
        const video = videoRef.current;
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        // Match canvas to video dimensions
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Check if any metric is actively changing
        const isMoving =
            Math.abs(currentMetrics.expression - prevMetricsRef.current.expression) > 2 ||
            Math.abs(currentMetrics.movement - prevMetricsRef.current.movement) > 2 ||
            Math.abs(currentMetrics.pitch - prevMetricsRef.current.pitch) > 2 ||
            Math.abs(currentMetrics.posture - prevMetricsRef.current.posture) > 2;

        prevMetricsRef.current = currentMetrics;

        const color = isMoving ? '#10b981' : '#ef4444'; // Green if moving, red if static

        if (landmarks && landmarks.length > 0) {
            // Calculate face bounding box from actual landmarks
            const xs = landmarks.map(l => l.x * canvas.width);
            const ys = landmarks.map(l => l.y * canvas.height);

            const minX = Math.min(...xs);
            const maxX = Math.max(...xs);
            const minY = Math.min(...ys);
            const maxY = Math.max(...ys);

            const padding = 20;
            const faceX = Math.max(0, minX - padding);
            const faceY = Math.max(0, minY - padding);
            const faceWidth = Math.min(canvas.width - faceX, maxX - minX + padding * 2);
            const faceHeight = Math.min(canvas.height - faceY, maxY - minY + padding * 2);

            // Draw FACE box
            ctx.strokeStyle = color;
            ctx.lineWidth = 3;
            ctx.setLineDash([10, 5]);
            ctx.strokeRect(faceX, faceY, faceWidth, faceHeight);

            // Face label
            ctx.fillStyle = color;
            ctx.font = 'bold 14px Arial';
            ctx.fillText('FACE', faceX + 10, faceY + 20);

            // --- INFERRED POSTURE VISUALIZATION ---
            // Landmarks: 1=Nose, 152=Chin, 33=LeftEye, 263=RightEye
            const nose = landmarks[1];
            const chin = landmarks[152];
            const leftEye = landmarks[33];
            const rightEye = landmarks[263];

            if (nose && chin && leftEye && rightEye) {
                // Calculate geometric properties
                const eyeDX = (rightEye.x - leftEye.x) * canvas.width;
                const eyeDY = (rightEye.y - leftEye.y) * canvas.height;
                const faceAngle = Math.atan2(eyeDY, eyeDX); // Head tilt in radians
                const faceTiltDeg = faceAngle * (180 / Math.PI);

                const chinX = chin.x * canvas.width;
                const chinY = chin.y * canvas.height;
                const faceW = Math.sqrt(eyeDX * eyeDX + eyeDY * eyeDY) * 2.5;

                // --- HARMONY & TENSION DETECTION ---
                // Harmony = Head is level (close to 0 degrees tilt)
                const isHarmonious = Math.abs(faceTiltDeg) < 5;
                const isTense = currentMetrics.posture < 60; // Tense if posture score is low

                // Dynamic Posture Color: Green (Harmonious), Blue (Cool), Red (Tense)
                let postureColor = isHarmonious ? '#22c55e' : '#38bdf8';
                if (isTense) postureColor = '#ef4444'; // Red alert for tension

                // Dynamic Shoulder Height (Alignment/Pride Simulation)
                // "Best fit... chin on the same line" -> High Score = Close (0.05)
                // "Over the line... too proud" -> Low Score = Far (0.4)
                const tensionFactor = Math.max(0, Math.min(100, currentMetrics.posture)) / 100; // 0..1
                const shoulderOffsetScale = 0.4 - (0.35 * tensionFactor);
                const shoulderOffsetY = faceHeight * shoulderOffsetScale;

                // --- SHOULDER AXIS (Gravity Aligned - Horizontal) ---
                // User Goal: Align Head Tilt with Horizon
                const shoulderCenterX = chinX;
                const shoulderCenterY = chinY + shoulderOffsetY;

                const lineLen = faceW * 2.2;
                const dx = lineLen / 2;
                const dy = 0; // Horizontal line has no vertical component

                ctx.beginPath();
                ctx.setLineDash([]); // Ensure solid line for Axis
                ctx.moveTo(shoulderCenterX - dx, shoulderCenterY);
                ctx.lineTo(shoulderCenterX + dx, shoulderCenterY);
                ctx.strokeStyle = postureColor;
                ctx.lineWidth = 4;
                ctx.stroke();

                // --- SPINE LINE (Gravity/Center) ---
                // Vertical line passing through Chin (X Separation)
                // Represents ideal upright posture
                const spineTopY = chinY + faceHeight * 0.05; // Start slightly below chin (avoid mouth)
                const spineBottomY = chinY + faceHeight * 4; // Extend down

                ctx.beginPath();
                ctx.moveTo(chinX, spineTopY);
                ctx.lineTo(chinX, spineBottomY);
                ctx.strokeStyle = '#94a3b8'; // Neutral Gray for gravity line
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]);
                ctx.stroke();
                ctx.setLineDash([]); // Reset

                // --- INTERSECTION POINT ---
                ctx.beginPath();
                ctx.arc(chinX, chinY, 6, 0, 2 * Math.PI);
                ctx.fillStyle = isHarmonious ? '#22c55e' : '#f59e0b'; // Green or Amber
                ctx.fill();

                if (isHarmonious) {
                    ctx.fillStyle = '#22c55e';
                    ctx.font = 'bold 24px Arial';
                    ctx.fillText('+', chinX + 10, chinY - 10);
                }

                // Tension Label
                if (isTense) {
                    ctx.fillStyle = '#ef4444';
                    ctx.font = 'bold 16px Arial';
                    ctx.fillText('TENSION', shoulderCenterX + dx + 10, shoulderCenterY);
                }

                // --- EXPANDED BODY BOX ---
                // Encompass the lines but clamp top to spineTopY to avoid face overlap
                const bodyMinX = Math.min(shoulderCenterX - dx, shoulderCenterX + dx, chinX) - 30;
                const bodyMaxX = Math.max(shoulderCenterX - dx, shoulderCenterX + dx, chinX) + 30;

                // Strict top edge: Start at spineTopY (below chin)
                const bodyMinY = spineTopY;
                const bodyMaxY = Math.max(shoulderCenterY + dy, spineBottomY) + 30;

                const finalBodyX = Math.max(0, bodyMinX);
                const finalBodyY = Math.max(0, bodyMinY);
                const finalBodyW = Math.min(canvas.width - finalBodyX, bodyMaxX - bodyMinX);
                const finalBodyH = Math.min(canvas.height - finalBodyY, bodyMaxY - bodyMinY);

                // Draw EXPANDED HEAD+BODY box
                ctx.strokeStyle = postureColor;
                ctx.lineWidth = 2;
                ctx.setLineDash([15, 8]);
                ctx.strokeRect(finalBodyX, finalBodyY, finalBodyW, finalBodyH);

                // Label
                ctx.fillStyle = postureColor;
                ctx.font = 'bold 14px Arial';
                ctx.fillText(isHarmonious ? 'HARMONY' : (isTense ? 'TENSION' : 'POSTURE'), finalBodyX + 10, finalBodyY + 20);
            } else {
                // Fallback Simple Body Box
                const bodyX = Math.max(0, faceX - faceWidth * 0.5);
                const bodyY = faceY + faceHeight * 0.8;
                const bodyWidth = Math.min(canvas.width - bodyX, faceWidth * 2);
                const bodyHeight = Math.min(canvas.height - bodyY, faceHeight * 2);

                ctx.strokeStyle = color;
                ctx.lineWidth = 2;
                ctx.setLineDash([15, 8]);
                ctx.strokeRect(bodyX, bodyY, bodyWidth, bodyHeight);
                ctx.fillStyle = color;
                ctx.fillText('BODY', bodyX + 10, bodyY + 20);
            }

            // Status label
            ctx.fillStyle = color;
            ctx.font = 'bold 16px Arial';
            ctx.fillText(isMoving ? 'TRACKING' : 'STATIC', faceX + 10, faceY + faceHeight - 10);
        } else {
            // Fallback to centered boxes if no landmarks
            const boxWidth = canvas.width * 0.4;
            const boxHeight = canvas.height * 0.6;
            const x = (canvas.width - boxWidth) / 2;
            const y = (canvas.height - boxHeight) / 2;

            ctx.strokeStyle = color;
            ctx.lineWidth = 3;
            ctx.setLineDash([10, 5]);
            ctx.strokeRect(x, y, boxWidth, boxHeight);

            ctx.fillStyle = color;
            ctx.font = 'bold 16px Arial';
            ctx.fillText(isMoving ? 'TRACKING' : 'STATIC', x + 10, y + 25);
        }
    };

    const handleStart = async () => {
        console.log("🔥 CLICK: handleStart button clicked!");
        try {
            console.log("🚀 Calling initializeCamera()...");
            await initializeCamera();
            console.log("✅ Camera initialized. Starting loop...");
            startLoop();
        } catch (err) {
            console.error("❌ handleStart failed:", err);
            alert("Erro ao iniciar câmera: " + err.message);
        }
    };

    const startTest = () => {
        setIsLive(true);
        historyRef.current = []; // Clear history ref
        setHistory([]);
        setTimeLeft(60);
        setShowResults(false);
    };

    const handleReset = () => {
        setIsLive(false);
        setShowResults(false);
        setMetrics({ expression: 0, movement: 0, pitch: 0, posture: 50 });
        historyRef.current = [];
        setHistory([]);
        setTimeLeft(60);
        // Restart camera
        initializeCamera().then(() => {
            startLoop();
        });
    };

    // --- CLIP RECORDING (5s at 30s mark) ---
    const startClipRecording = () => {
        if (!videoRef.current || !videoRef.current.srcObject) return;
        console.log('🎥 Starting 5s Clip Recording...');
        setIsRecording(true);
        recordedChunksRef.current = [];

        try {
            const stream = videoRef.current.srcObject;
            const options = { mimeType: 'video/webm;codecs=vp9,opus' };
            const recorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) recordedChunksRef.current.push(e.data);
            };

            recorder.onstop = () => {
                console.log('✅ Clip Recording Complete');
                setIsRecording(false);
            };

            recorder.start();
            setTimeout(() => {
                if (recorder.state === 'recording') recorder.stop();
            }, 5000); // Record for 5 seconds
        } catch (err) {
            console.error('Recording failed:', err);
            setIsRecording(false);
        }
    };

    // --- SAVE RESULT ---
    const handleSaveResult = async () => {
        if (!user) {
            alert('Please login to save your results to your profile!');
            return;
        }

        const avgScore = getGrade();
        const finalMetrics = {
            expression: getAverage('expression'),
            movement: getAverage('movement'),
            pitch: getAverage('pitch'),
            posture: getAverage('posture')
        };

        // Prepare Blob
        let base64Frame = null;
        if (recordedChunksRef.current.length > 0) {
            const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
            const reader = new FileReader();
            base64Frame = await new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        }

        try {
            const token = localStorage.getItem('token');
            const res = await fetch('https://plexo-natural-api.onrender.com/api/save-performance', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    metrics: finalMetrics,
                    avgScore,
                    frame: base64Frame, // Sends base64 video/snapshot
                    type: 'performance_test'
                })
            });

            const data = await res.json();
            if (data.success) {
                alert('Performance Saved Successfully! Check your profile.');
            } else {
                alert('Error saving: ' + (data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Save failed:', err);
            alert('Failed to save result.');
        }
    };

    useEffect(() => {
        if (isLive && timeLeft > 0) {
            const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
            return () => clearInterval(timer);
        } else if (isLive && timeLeft === 0) {
            setIsLive(false);
            setShowResults(true);
        }

        // Trigger recording at 30 seconds
        if (isLive && timeLeft === 30 && !isRecording) {
            startClipRecording();
        }
    }, [isLive, timeLeft]);

    const getAverage = (key) => {
        if (historyRef.current.length === 0) return 0;
        const sum = historyRef.current.reduce((acc, m) => acc + m[key], 0);
        return Math.round(sum / historyRef.current.length);
    };

    const getGrade = () => {
        const avg = (getAverage('expression') + getAverage('movement') + getAverage('pitch') + getAverage('posture')) / 4;
        return Math.round(avg);
    };

    const toggleFullscreen = () => {
        if (!videoContainerRef.current) return;

        if (!document.fullscreenElement) {
            videoContainerRef.current.requestFullscreen().then(() => {
                setIsFullscreen(true);
            }).catch(err => {
                console.error(`Error attempting to enable fullscreen: ${err.message}`);
            });
        } else {
            document.exitFullscreen().then(() => {
                setIsFullscreen(false);
            });
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 text-white p-4 font-sans">
            {/* Ad Placeholder Left */}
            <div className="fixed left-0 top-20 bottom-0 w-32 hidden lg:flex flex-col items-center justify-center pointer-events-none" style={{ zIndex: 5 }}>
                <AdPlaceholder width="120px" height="600px" text="Ad Space Left" />
            </div>

            {/* Ad Placeholder Right */}
            <div className="fixed right-0 top-20 bottom-0 w-32 hidden lg:flex flex-col items-center justify-center pointer-events-none" style={{ zIndex: 5 }}>
                <AdPlaceholder width="120px" height="600px" text="Ad Space Right" />
            </div>

            {/* Navbar replaces custom header */}
            <Navbar />

            {/* Adjusted padding-top (pt-24) to account for Navbar height */}
            <main className="pt-24 max-w-5xl mx-auto relative z-10">
                {!cameraReady ? (
                    <div className="text-center py-20 animate-fade-in">
                        <div className="inline-block p-6 rounded-full bg-gray-800 mb-8 shadow-2xl shadow-indigo-500/10">
                            <Camera size={64} className="text-indigo-400" />
                        </div>
                        <h2 className="text-4xl font-bold mb-4">Performance Art Test</h2>
                        <p className="text-gray-400 mb-8 max-w-md mx-auto">
                            Analyze your expressions, movements, pitch, and posture in real-time using AI.
                        </p>
                        <button
                            onClick={handleStart}
                            disabled={isLoading}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 disabled:opacity-50 shadow-lg shadow-indigo-600/20"
                        >
                            {isLoading ? 'Loading AI Models...' : 'Start Camera'}
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Video Feed */}
                        <div className="lg:col-span-2 space-y-4">
                            <div
                                ref={videoContainerRef}
                                className="relative rounded-3xl overflow-hidden shadow-2xl border border-gray-700 aspect-video bg-black group"
                                style={{ zIndex: 10 }}
                            >
                                {!isLive && !showResults && (
                                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                                        <div className="text-center">
                                            <button
                                                onClick={startTest}
                                                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
                                            >
                                                Start 60s Performance
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {showResults && (
                                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-900/10 backdrop-blur-[2px]">
                                        {/* Minimal overlay to indicate finished state if needed, or nothing */}
                                    </div>
                                )}

                                <video
                                    ref={videoRef}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
                                    muted
                                    playsInline
                                />

                                {/* Canvas for Bounding Box */}
                                <canvas
                                    ref={canvasRef}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        pointerEvents: 'none',
                                        transform: 'scaleX(-1)'
                                    }}
                                />

                                {/* Fullscreen Toggle Button */}
                                {cameraReady && (
                                    <button
                                        onClick={toggleFullscreen}
                                        style={{
                                            position: 'absolute',
                                            top: '15px',
                                            right: '15px',
                                            background: 'rgba(0, 0, 0, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '8px',
                                            padding: '8px',
                                            cursor: 'pointer',
                                            zIndex: 50,
                                            color: 'white',
                                            transition: 'background 0.3s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
                                    >
                                        {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
                                    </button>
                                )}

                                {isLive && (
                                    <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse flex items-center gap-2">
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                        LIVE {timeLeft}s
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Metrics Panel */}
                        <div style={{ background: '#1e293b', borderRadius: '24px', padding: '24px', border: '1px solid #334155' }}>
                            {showResults ? (
                                <div className="animate-fade-in text-center">
                                    <h3 className="text-2xl font-bold mb-2">Performance Complete</h3>
                                    <div className="mb-6 py-4">
                                        <span className="text-7xl font-bold bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                                            {getGrade()}
                                        </span>
                                        <span className="text-gray-400 text-2xl display-block">/100</span>
                                        <p className="text-gray-400 mt-2">Final Grade</p>
                                    </div>

                                    <div className="space-y-4 mb-8 text-left">
                                        <MetricBar label="Expression" value={getAverage('expression')} icon={Smile} color="#38bdf8" />
                                        <MetricBar label="Movement" value={getAverage('movement')} icon={Zap} color="#f59e0b" />
                                        <MetricBar label="Pitch" value={getAverage('pitch')} icon={Music} color="#ec4899" />
                                        <MetricBar label="Posture" value={getAverage('posture')} icon={User} color="#22c55e" />
                                    </div>

                                    <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 mb-8 text-left">
                                        <h4 className="font-bold text-blue-400 mb-2 flex items-center gap-2">
                                            Analysis
                                        </h4>
                                        <p className="text-sm text-blue-200">
                                            {getGrade() > 70
                                                ? "Excellent performance! High engagement across all metrics."
                                                : "Good effort. Try to increase movement range and expression intensity."}
                                        </p>
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        <button onClick={handleReset} className="w-full bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-bold transition-all">
                                            Try Again
                                        </button>
                                        <button
                                            onClick={handleSaveResult}
                                            className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-green-600/20 flex items-center justify-center gap-2"
                                        >
                                            {user ? 'Save Result to Profile' : 'Login to Save Result'}
                                        </button>
                                        <button
                                            onClick={() => setIsPaymentModalOpen(true)}
                                            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2"
                                        >
                                            <Zap size={18} className="text-yellow-400" />
                                            Unlock Premium AI Analysis
                                        </button>
                                    </div>
                                    <PaymentModal
                                        isOpen={isPaymentModalOpen}
                                        onClose={() => setIsPaymentModalOpen(false)}
                                        amount={1.99}
                                    />
                                </div>
                            ) : (
                                <div className="animate-fade-in">
                                    <h3 style={{ fontSize: '1.2rem', marginBottom: '20px', fontWeight: 'bold' }}>Real-time Sensing</h3>

                                    <MetricBar label="Expression" value={metrics.expression} icon={Smile} color="#38bdf8" />
                                    <MetricBar label="Movement" value={metrics.movement} icon={Zap} color="#f59e0b" />

                                    <div className="mb-4">
                                        <div className="flex justify-between mb-1">
                                            <span className="text-sm font-medium text-white flex items-center gap-2">
                                                <Music size={16} /> Pitch
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {pitchScore > 20 && <span className="text-xs text-pink-400 font-bold animate-pulse">Note Detected</span>}
                                                <span className="text-sm font-medium text-white">{pitchHz} Hz</span>
                                            </div>
                                        </div>
                                        <div className="w-full bg-gray-700 rounded-full h-2.5 relative">
                                            <div
                                                className="h-2.5 rounded-full transition-all duration-100"
                                                style={{ width: `${Math.min(100, (pitchHz / 1000) * 100)}%`, backgroundColor: '#ec4899' }}
                                            ></div>
                                            {pitchScore > 20 && (
                                                <div
                                                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg border-2 border-pink-500 transition-all duration-100"
                                                    style={{ left: `${Math.min(100, (pitchHz / 1000) * 100)}%` }}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <MetricBar label="Posture" value={metrics.posture} icon={User} color="#22c55e" />

                                    <div className="mt-8 p-4 bg-gray-800 rounded-xl border border-gray-700">
                                        <h4 className="text-gray-400 text-sm mb-2">Live Analysis</h4>
                                        <div className="space-y-2">
                                            <p className="text-sm">
                                                <span className="text-gray-500">Expression:</span>
                                                {metrics.expression > 50 ? ' High Intensity' : ' Neutral/Low'}
                                            </p>
                                            <p className="text-sm">
                                                <span className="text-gray-500">Movement:</span>
                                                {metrics.movement > 30 ? ' Active' : ' Stable'}
                                            </p>
                                            <p className="text-sm">
                                                <span className="text-gray-500">Vocal:</span>
                                                {pitchHz > 0 ? ' Active' : ' Silent'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default PerformanceArtTest;
